/**
 * Typing store — drives the "agent is typing" indicator (text mode).
 *
 * Behavior lifted from the production widget:
 * - Dots appear the moment the user sends (we always expect a reply); the
 *   min-duration timer anchors only when they first appear, so back-to-back
 *   sends keep one continuous indicator instead of resetting it.
 * - Any landed backend response — text or RPC — answers the pending turn and
 *   drops the dots, unconditionally.
 * - Replies that land faster than MIN_TYPING_INDICATOR_MS wait out the
 *   remainder before committing, otherwise the indicator just flashes.
 */
import { createStore } from "./store.js";
import { MIN_TYPING_INDICATOR_MS } from "../constants.js";
export function createTypingStore(clock) {
    let shownAt = null;
    let isTyping = false;
    const build = () => ({ isTyping });
    const handle = createStore(build);
    const set = (next) => {
        if (isTyping === next)
            return;
        isTyping = next;
        handle.commit();
    };
    return {
        getSnapshot: handle.getSnapshot,
        subscribe: handle.subscribe,
        dispose: handle.dispose,
        showOnUserSend() {
            if (shownAt === null)
                shownAt = clock.now();
            set(true);
        },
        clear() {
            shownAt = null;
            set(false);
        },
        waitMinDuration() {
            if (shownAt === null)
                return Promise.resolve();
            const remaining = MIN_TYPING_INDICATOR_MS - (clock.now() - shownAt);
            if (remaining <= 0)
                return Promise.resolve();
            return new Promise((resolve) => clock.setTimeout(resolve, remaining));
        },
    };
}
